import React from 'react';

// =============================================================================
// CountPanel
// =============================================================================
// A dense, hierarchical, color-coded panel of clickable counts. Purely
// presentational: it renders a config + a counts map and reports clicks via
// onSelect — it knows nothing about any particular dashboard. Callers supply the
// config (sections/rows -> keys, with labels/indent/color) and the live counts.
//
// Layout: buckets reflow into as many columns as the container width allows
// (responsive, container-driven) and stretch to fill that width. They flow
// COLUMN-MAJOR (top-to-bottom, then into the next column), so a sorted list stays
// in order down each column rather than wrapping across rows. The flow unit is
// a "block": a headed section stays whole (its label groups its buckets), while a
// header-less section splits into one block per top-level bucket — with deeper-
// indented sub-buckets kept attached to their parent so a group never splits across
// columns. Narrow container -> a single column; wider -> 2, 3, … columns.
//
// Used by the LC dashboard's "counts" view and by Accounting Dashboards, and intended
// to be reusable anywhere a movable/grouped count summary is wanted (e.g. a future
// dispatch-dashboard stats replacement).

export type CountColor = 'red' | 'orange' | 'yellow' | 'green';

export interface CountRow {
  key: string;          // identifies this count (matches the counts map + drill-down target)
  label: string;
  indent?: number;      // nesting depth for sub-rows (0 = flush)
  bold?: boolean;       // emphasize sub-totals
  color?: CountColor;   // optional count-chip color (semantic; only shown when count > 0)
  headerStyle?: boolean; // render this column row as a dark header bar (e.g. a sub-section)
}

export interface CountColumn {
  // The section's own (broad) count, rendered as a dark header bar. Optional: a
  // single-section panel (e.g. one whose surrounding tile title already names it)
  // can omit the header and render just its rows.
  header?: CountRow;
  rows: CountRow[];
}

export interface CountPanelConfig {
  key: string;
  title: string;            // panel title bar
  columns: CountColumn[];   // logical sections (see Block-building below)
  footers?: CountRow[];     // full-width rows beneath the columns
  /** Render a dollar column beside each count, from the `amounts` prop. Opt-in per
   *  panel: V1 shows money on its CMS and Accessorials blocks only
   *  (dashboard_accounting.pl `AcctTotal:*`), and a count-only panel that grew a blank
   *  money column would just read as missing data. */
  showAmounts?: boolean;
}

// Severity a whole panel can report to its surrounding tile, worst-first. Green is
// deliberately absent: it flags a GOOD state (work done), so it never raises severity.
export type CountSeverity = 'red' | 'orange' | 'yellow';
const SEVERITY_PRIORITY: CountSeverity[] = ['red', 'orange', 'yellow'];

export interface CountPanelSummary {
  /** Roll-up of the panel's buckets, counting each order once (see summarizeCountPanel). */
  total: number;
  /** Worst severity flagged by any bucket, or null when nothing is flagged. */
  severity: CountSeverity | null;
}

/**
 * Summarize a panel for a collapsed tile header: the roll-up count to show after the
 * title, and the tone the header bar should take.
 *
 * TOTAL — counts each order once rather than adding every visible number, because the
 * configs are hierarchical: a headed section's dark header IS the section's own broad
 * count (its rows are the breakdown), and an indented row is a subset of the row above
 * it. So a headed section contributes its header, and a headerless one contributes its
 * indent-0 rows. Footers are full-width roll-ups of the columns, so they're excluded.
 *
 * SEVERITY — considers EVERY bucket (headers, indented rows, footers), and only when its
 * count is > 0, mirroring the chip-coloring rule in renderRow: a 0 has nothing to flag.
 *
 * Returns null when NONE of the panel's keys are in the counts map — a panel that has
 * been collapsed since page load never ran its query, and reporting "(0)" would lie.
 */
export const summarizeCountPanel = (
  config: CountPanelConfig,
  counts: { [key: string]: number },
): CountPanelSummary | null => {
  let anyKnown = false;
  let total = 0;
  const flagged = new Set<CountSeverity>();

  const read = (row: CountRow): number => {
    const v = counts[row.key];
    if (typeof v !== 'number') return 0;
    anyKnown = true;
    // Only a non-zero count flags anything, matching the rendered chip.
    if (v > 0 && row.color && row.color !== 'green') flagged.add(row.color);
    return v;
  };

  for (const col of config.columns) {
    if (col.header) {
      total += read(col.header);
      // Rows of a headed section are its breakdown — read for severity only.
      for (const row of col.rows) read(row);
      continue;
    }
    for (const row of col.rows) {
      const v = read(row);
      if ((row.indent || 0) === 0) total += v;
    }
  }
  // Footers roll up the columns above them — severity only, never added to the total.
  for (const f of config.footers || []) read(f);

  if (!anyKnown) return null;
  return { total, severity: SEVERITY_PRIORITY.find((s) => flagged.has(s)) ?? null };
};

const COUNT_COLOR_CLASSES: Record<CountColor, string> = {
  red: 'bg-red-600 text-white',
  orange: 'bg-orange-500 text-white',
  yellow: 'bg-yellow-400 text-black',
  green: 'bg-green-600 text-white',
};

// Minimum block width before another column is added. min(…, 100%) keeps a single
// block from overflowing a container narrower than this.
const BLOCK_MIN_WIDTH = 240;

export interface CountPanelProps {
  config: CountPanelConfig;
  counts: { [key: string]: number };
  /** Dollar total per key. Only read when the config sets `showAmounts`. */
  amounts?: { [key: string]: number };
  loading: boolean;
  onSelect: (key: string) => void;
  // When true, render bare (no outer border/shadow/min-width and no title bar) so a
  // surrounding tile (e.g. <DashboardTile>) can provide the chrome and drag handle.
  embedded?: boolean;
  /**
   * Shown for a key that is absent from `counts` while not loading. Defaults to '0'
   * (LC's behavior). A page whose data source is not wired yet passes '—' so a tile never
   * shows a false zero.
   */
  emptyValue?: string;
}

// A flow unit that never splits across columns.
interface Block {
  header?: CountRow;
  rows: CountRow[];
}

// Turn the config's sections into reflowable blocks. A headed section is one block
// (keep its buckets under their label); a header-less section splits into one block
// per top-level (indent 0) bucket, with deeper-indented rows attached to it.
const buildBlocks = (columns: CountColumn[]): Block[] => {
  const blocks: Block[] = [];
  for (const col of columns) {
    if (col.header) {
      blocks.push({ header: col.header, rows: col.rows });
      continue;
    }
    let cur: Block | null = null;
    for (const r of col.rows) {
      if (!cur || (r.indent || 0) === 0) {
        cur = { rows: [r] };
        blocks.push(cur);
      } else {
        cur.rows.push(r);
      }
    }
  }
  return blocks;
};

const CountPanel: React.FC<CountPanelProps> = ({ config, counts, amounts, loading, onSelect, embedded = false, emptyValue = '0' }) => {
  const fmt = (n: number | null | undefined) => (typeof n !== 'number' ? (loading ? '…' : emptyValue) : n.toLocaleString());
  // Whole dollars, as V1 prints them — a dashboard column is for comparing magnitudes,
  // and cents across nine rows is noise.
  const money = (n: number | null | undefined) =>
    typeof n !== 'number' ? (loading ? '…' : '') : `$${Math.round(n).toLocaleString()}`;
  const withAmounts = !!config.showAmounts;

  const renderRow = (row: CountRow, variant: 'header' | 'row' | 'footer') => {
    const dark = variant !== 'row';
    const value = counts[row.key];
    // Only color the chip when there's actually something to flag — a 0 count has
    // nothing to warn/error about, so it stays neutral regardless of row.color.
    const showColor = row.color && typeof value === 'number' && value > 0;
    const countCls = showColor
      ? COUNT_COLOR_CLASSES[row.color!]
      : dark
      ? 'text-white'
      : 'text-body';
    return (
      <button
        key={row.key}
        type="button"
        onClick={() => onSelect(row.key)}
        title={`View ${row.label}`}
        className={`w-full flex items-center justify-between gap-2 text-left text-sm px-2 py-1 transition-colors ${
          dark
            ? 'bg-slate-700 text-white font-semibold hover:bg-slate-600'
            : 'text-gray-800 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-slate-700/40'
        }`}
      >
        <span
          className={`truncate ${row.bold ? 'font-semibold' : ''}`}
          style={{ paddingLeft: `${(row.indent || 0) * 14}px` }}
        >
          {row.label}
        </span>
        <span className={`shrink-0 tabular-nums font-semibold rounded px-1.5 ${countCls}`}>
          {fmt(value)}
        </span>
        {withAmounts && (
          // Fixed width so the dollar column lines up down the panel rather than
          // ragging with each row's label length.
          <span className={`shrink-0 w-24 text-right tabular-nums ${dark ? 'text-white' : 'text-body'}`}>
            {money(amounts?.[row.key])}
          </span>
        )}
      </button>
    );
  };

  const blocks = buildBlocks(config.columns);

  return (
    <div
      className={
        embedded
          ? // Fill the tile width so the blocks have the full container to flow into;
            // natural height keeps the tile's double-click fit-to-content measurable.
            'w-full bg-panel'
          : 'rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden bg-panel shadow-sm min-w-[280px]'
      }
    >
      {!embedded && (
        <div className="bg-slate-800 text-white text-center font-bold text-sm py-1.5">{config.title}</div>
      )}
      {/* CSS multi-column so blocks flow top-to-bottom then into the next column
          (column-major) — a sorted list stays in order DOWN each column rather than
          across rows. The column count is still container-driven (one per BLOCK_MIN_WIDTH
          of width), so it reflows responsively. break-inside-avoid keeps a block whole;
          per-block right/bottom borders draw the column/row separators (a column's
          equal-width blocks align their right edges into one rule). */}
      <div style={{ columnWidth: `${BLOCK_MIN_WIDTH}px`, columnGap: 0 }}>
        {blocks.map((block, i) => (
          <div
            key={block.header?.key ?? block.rows[0]?.key ?? i}
            className="break-inside-avoid border-b border-r border-gray-200 dark:border-gray-700 bg-panel"
          >
            {block.header && renderRow(block.header, 'header')}
            <div className="flex flex-col divide-y divide-gray-100 dark:divide-gray-700/50">
              {block.rows.map((r) => renderRow(r, r.headerStyle ? 'header' : 'row'))}
            </div>
          </div>
        ))}
      </div>
      {config.footers && config.footers.length > 0 && (
        <div className="border-t border-gray-300 dark:border-gray-600">
          {config.footers.map((f) => renderRow(f, 'footer'))}
        </div>
      )}
    </div>
  );
};

export default CountPanel;
